/**
 * El ambiente en que corre la UI, tal como se le muestra al operador.
 *
 * La etiqueta es texto libre de `VITE_ENVIRONMENT_LABEL` (`local`, `desarrollo`,
 * `staging`, `produccion`…). Aca se interpreta para decidir cuanto tiene que
 * hacerse notar: produccion en rojo, los servidores compartidos en ambar, el
 * resto neutro. En una herramienta con reproceso manual, reconocer produccion no
 * puede depender de como alguien escribio la etiqueta en el `.env`.
 *
 * Es la unica interpretacion de la etiqueta: la insignia del menu, el titulo de
 * la pestana y el favicon (`app/favicon.ts`) salen de aca, asi no pueden
 * contradecirse.
 */

/**
 * `staging` agrupa a los servidores compartidos que no son produccion: el
 * staging propiamente tal y el servidor de desarrollo de DigitalOcean
 * (`desarrollo` en docker-compose.server.yml). Se separan de `local` a
 * proposito: la pestana del servidor y la del computador propio no pueden verse
 * iguales.
 */
export type EnvironmentKind = 'production' | 'staging' | 'local' | 'other';

/** Nombre de la app, para la pestana y la marca. */
export const APP_NAME = 'Middle';

interface KnownEnvironment {
  readonly kind: Exclude<EnvironmentKind, 'other'>;
  /** Nombre visible, en espanol. */
  readonly name: string;
  /** Version corta, para el menu contraido a iconos. */
  readonly short: string;
}

const PRODUCTION: KnownEnvironment = { kind: 'production', name: 'Producción', short: 'PROD' };
const STAGING: KnownEnvironment = { kind: 'staging', name: 'Staging', short: 'STG' };
const DEVELOPMENT: KnownEnvironment = { kind: 'staging', name: 'Desarrollo', short: 'DEV' };
const LOCAL: KnownEnvironment = { kind: 'local', name: 'Local', short: 'LOCAL' };

/** Etiquetas conocidas, ya normalizadas (ver `normalize`). */
const KNOWN_ENVIRONMENTS: ReadonlyMap<string, KnownEnvironment> = new Map([
  ['produccion', PRODUCTION],
  ['production', PRODUCTION],
  ['prod', PRODUCTION],
  ['staging', STAGING],
  ['stage', STAGING],
  ['desarrollo', DEVELOPMENT],
  ['development', DEVELOPMENT],
  ['local', LOCAL],
]);

/** Minusculas, sin espacios en los bordes y sin tildes: `Producción` = `produccion`. */
function normalize(label: string): string {
  return label.trim().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

function known(label: string): KnownEnvironment | undefined {
  return KNOWN_ENVIRONMENTS.get(normalize(label));
}

export function classifyEnvironment(label: string): EnvironmentKind {
  return known(label)?.kind ?? 'other';
}

/** Nombre visible: los ambientes conocidos con su nombre, el resto tal cual vino. */
export function environmentDisplayName(label: string): string {
  return known(label)?.name ?? label.trim();
}

/** Version corta, para el menu contraido a iconos. */
export function environmentShortName(label: string): string {
  return known(label)?.short ?? label.trim().slice(0, 5).toUpperCase();
}

/**
 * Titulo de la pestana: `Mensajes · Middle`, con el ambiente entre parentesis
 * cuando no es produccion. Con varias pestanas abiertas —un mensaje, su flujo,
 * las dead letters— es lo que permite encontrar cada una.
 */
export function formatDocumentTitle(section: string, environmentLabel: string): string {
  const title = `${section} · ${APP_NAME}`;
  return classifyEnvironment(environmentLabel) === 'production'
    ? title
    : `${title} (${environmentLabel.trim()})`;
}
